import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import NavBar from "../common/NavBar";
import Footer from "../common/Footer";
import { useTranslation } from "react-i18next";

const BlogDetail = () => {
  const { t, i18n } = useTranslation();
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const getApiBaseUrl = () =>
    process.env.REACT_APP_API_URL || "http://localhost:8000";

  const currentLanguage = ["en", "de", "es"].includes((i18n.language || "en").slice(0, 2).toLowerCase())
    ? (i18n.language || "en").slice(0, 2).toLowerCase()
    : "en";

  useEffect(() => {
    const fetchArticle = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(
          `${getApiBaseUrl()}/api/blog/articles/${slug}/?language=${currentLanguage}`
        );
        if (response.status === 404) {
          navigate("/blog", { replace: true });
          return;
        }
        if (!response.ok) throw new Error(t("error_fetching_posts"));
        const data = await response.json();
        setArticle(data);
        // Update document title and meta description for SEO
        if (data.meta_title) document.title = data.meta_title;
        else document.title = data.title;
        let metaDesc = document.querySelector('meta[name="description"]');
        if (!metaDesc) {
          metaDesc = document.createElement("meta");
          metaDesc.setAttribute("name", "description");
          document.head.appendChild(metaDesc);
        }
        if (data.meta_description) {
          metaDesc.setAttribute("content", data.meta_description);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchArticle();
  }, [slug, navigate, t, currentLanguage]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <NavBar />

      <main className="flex-1 pt-20 pb-12 md:pb-16">
        {loading && (
          <p className="text-center text-gray-500 py-32">{t("loading")}</p>
        )}
        {error && (
          <p className="text-center text-red-500 py-32">{error}</p>
        )}
        {!loading && !error && article && (
          <article className="max-w-3xl mx-auto px-4 md:px-6">
            {/* Back link */}
            <div className="mb-6">
              <Link
                to="/blog"
                className="text-blue-500 hover:text-blue-700 text-sm font-medium"
              >
                &larr; {t("blog")}
              </Link>
            </div>

            {/* Cover image */}
            {article.cover_image_url && (
              <img
                src={article.cover_image_url}
                alt={article.title}
                className="w-full max-h-80 object-cover rounded-xl mb-8 shadow"
              />
            )}

            {/* Tags */}
            {article.tags && article.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4">
                {article.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/blog?tag=${encodeURIComponent(tag)}`}
                    className="bg-blue-100 text-blue-700 text-xs px-3 py-1 rounded-full hover:bg-blue-200 transition"
                  >
                    {tag}
                  </Link>
                ))}
              </div>
            )}

            {/* Title */}
            <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-3 leading-tight">
              {article.title}
            </h1>

            {/* Author / Date */}
            <div className="flex items-center gap-3 text-sm text-gray-400 mb-8">
              {article.author_name && (
                <span>
                  {t("by_author")}{" "}
                  <span className="text-gray-600 font-medium">
                    {article.author_name}
                  </span>
                </span>
              )}
              <span>&middot;</span>
              <span>
                {t("published_on")} {formatDate(article.published_date)}
              </span>
            </div>

            {/* Content */}
            <div
              className="prose prose-blue max-w-none text-gray-700 leading-relaxed
                prose-h1:text-blue-600 prose-h2:text-blue-600 prose-h3:text-gray-700
                prose-a:text-blue-500 prose-a:underline
                prose-blockquote:border-l-blue-400 prose-blockquote:text-gray-500"
              dangerouslySetInnerHTML={{
                __html: article.clean_content || article.content,
              }}
            />

            {/* Bottom back link */}
            <div className="mt-12 pt-8 border-t border-gray-200">
              <Link
                to="/blog"
                className="inline-block bg-blue-500 hover:bg-blue-600 text-white px-5 py-2 rounded-lg text-sm font-medium transition"
              >
                &larr; {t("blog")}
              </Link>
            </div>
          </article>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default BlogDetail;
